import type { DraftRequestInput, RequestStatus, SubmitRequestInput } from '@pr-app/shared';
import type { Attachment } from './attachments';
import { api } from './client';

export interface DraftSaveResult {
  id: string;
  requestNo: number;
  status: RequestStatus;
  updatedAt: string;
}

/** Auto-save endpoint (#31) — safe to call repeatedly; `draft.id` makes it an upsert. */
export const saveDraft = (draft: DraftRequestInput): Promise<DraftSaveResult> =>
  api<DraftSaveResult>('/api/requests/drafts', {
    method: 'POST',
    body: JSON.stringify(draft),
  });

/**
 * Submit for review (#32/#33). Still a 501 stub server-side pending Sprint 3 —
 * wired now so the button works unmodified once that lands. The response
 * shape isn't settled yet, so callers only care whether the call succeeded.
 */
export const submitRequest = (id: string, payload: SubmitRequestInput): Promise<unknown> =>
  api<unknown>(`/api/requests/${encodeURIComponent(id)}/submit`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export interface RequestSummary {
  id: string;
  requestNo: number;
  status: RequestStatus;
  purchaseType: string | null;
  companyCode: string | null;
  vendorAccount: string | null;
  currencyCode: string | null;
  requesterAadId: string;
  /**
   * Every dashboard-shaped endpoint (`GET /api/requests`, `/api/review/dashboard`,
   * `/api/admin/dashboard`) has always sent these two (#220) — only surfaced on
   * this type now that the admin dashboard (#224) needs to render them as a
   * column, rather than each caller re-declaring them locally.
   */
  requesterName: string;
  requesterEmail: string;
  title: string;
  completeness: string;
  daysUntilExpiry: number | null;
  updatedAt: string;
  total: string;
  d365PrNumber: string | null;
  d365Link: string | null;
  _count: { lines: number; attachments: number };
  /**
   * Present only from `GET /api/review/dashboard` (#222) — true for the
   * viewer's own request, present but never actionable through that list.
   * `undefined` on the requester's own dashboard, where every row is the
   * viewer's own by construction.
   */
  isOwn?: boolean;
  /**
   * Present only from `GET /api/admin/dashboard` (#224) — who most recently
   * approved or rejected this request, and when. `null` while the request is
   * still pending; `undefined` on every other dashboard endpoint.
   */
  decidedByName?: string | null;
  decidedByEmail?: string | null;
  decisionAction?: 'APPROVED' | 'REJECTED' | null;
  decidedAt?: string | null;
}

export type DashboardSortField = 'requestNo' | 'total' | 'updatedAt';
export type DashboardSortOrder = 'asc' | 'desc';
export type DashboardDateRange = 'last30' | 'last90' | 'year' | 'custom';

/** Everything the dashboard (#152, wireframe R1/R1c) can ask the list endpoint
 * to do — search, filter, sort and page. Every field optional; omitting all of
 * them returns the server's own defaults (see `apps/api`'s route docs). */
export interface ListRequestsParams {
  q?: string;
  status?: RequestStatus[];
  company?: string[];
  range?: DashboardDateRange;
  from?: string;
  to?: string;
  sort?: DashboardSortField;
  order?: DashboardSortOrder;
  page?: number;
  pageSize?: number;
}

export interface RequestCounts {
  total: number;
  draft: number;
  pendingReview: number;
  approved: number;
  prCreated: number;
  rejected: number;
}

export interface ListRequestsResult {
  requests: RequestSummary[];
  total: number;
  page: number;
  pageSize: number;
  counts: RequestCounts;
  companies: string[];
}

function buildListQuery(params: ListRequestsParams): string {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.status && params.status.length > 0) search.set('status', params.status.join(','));
  if (params.company && params.company.length > 0) search.set('company', params.company.join(','));
  if (params.range) search.set('range', params.range);
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.sort) search.set('sort', params.sort);
  if (params.order) search.set('order', params.order);
  if (params.page) search.set('page', String(params.page));
  if (params.pageSize) search.set('pageSize', String(params.pageSize));
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/** Dashboard list (#33, #152) — never includes a deleted draft. Search, filter,
 * sort and paging are all server-side; see `ListRequestsParams`. */
export const listRequests = (params: ListRequestsParams = {}): Promise<ListRequestsResult> =>
  api<ListRequestsResult>(`/api/requests${buildListQuery(params)}`);

/** Delete a draft (wireframe D2) — soft delete server-side; DRAFT only. */
export const deleteDraft = (id: string): Promise<void> =>
  api<void>(`/api/requests/${encodeURIComponent(id)}`, { method: 'DELETE' });

/**
 * Withdraw a submitted request (#149, wireframe 08.3 behaviour rule 2; frames
 * R1d, D2) — returns it to DRAFT and out of the reviewer queue. PENDING_REVIEW
 * only, server-enforced.
 */
export const withdrawRequest = (id: string): Promise<void> =>
  api<void>(`/api/requests/${encodeURIComponent(id)}/withdraw`, { method: 'POST' });

/**
 * Duplicate as new request (#152, wireframe R1d note 3) — copies header and lines
 * into a fresh Draft, from any status. Never copies attachments, status or audit
 * history. Returns the same shape as `saveDraft`, so the caller can navigate
 * straight to the new draft's edit route.
 */
export const duplicateRequest = (id: string): Promise<DraftSaveResult> =>
  api<DraftSaveResult>(`/api/requests/${encodeURIComponent(id)}/duplicate`, { method: 'POST' });

export interface RequestDetailLine {
  id: string;
  lineNo: number;
  itemDescription: string | null;
  procurementCategoryId: string | null;
  quantity: string | null;
  unitOfMeasure: string | null;
  netPrice: string | null;
  deliveryDate: string | null;
  startDate: string | null;
  endDate: string | null;
  /** D365 sales tax group code. */
  salesTaxGroupId: string | null;
  vendorItemDescription: string | null;
  vendorPartNo: string | null;
}

export interface RequestAuditEntry {
  action: string;
  actorAadId: string;
  actorName: string;
  actorEmail: string | null;
  comment: string | null;
  createdAt: string;
}

/** Full request — header, lines, attachments and audit trail (GET /api/requests/:id). */
export interface RequestDetail {
  id: string;
  requestNo: number;
  status: RequestStatus;
  purchaseType: string | null;
  companyCode: string | null;
  vendorAccount: string | null;
  currencyCode: string | null;
  requesterAadId: string;
  requesterName: string;
  requesterEmail: string;
  createdAt: string;
  updatedAt: string;
  projectId: string | null;
  projectCategoryId: string | null;
  mainAccountId: string | null;
  /** False when the deployment fixes the main account in its format — nothing to choose or require. */
  mainAccountRequired?: boolean;
  costCentreId: string | null;
  lineOfBusinessId: string | null;
  warehouseId: string | null;
  paymentTerms: string | null;
  termStartDate: string | null;
  termEndDate: string | null;
  deliveryDate: string | null;
  internalNotes: string | null;
  notesForVendor: string | null;
  d365PrNumber: string | null;
  lines: RequestDetailLine[];
  attachments: Attachment[];
  auditLog: RequestAuditEntry[];
}

/** One request in full (#41) — readable by its requester, any reviewer, or an admin. */
export const getRequest = (id: string): Promise<RequestDetail> =>
  api<RequestDetail>(`/api/requests/${encodeURIComponent(id)}`);
